import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BrandPlan, ContentStrategy, ContentStrategyType } from 'types'
import Styles from './StrategyAndContextSelection.module.scss'
import { StrategySelect } from './StrategySelect'
import { Input } from 'almanac-ui/TextInputField/Input'
import { AddContext } from './AddContext'
import { CampaignSource } from './Types'
import { Button } from 'blaze-ui/Button'
import { Settings } from 'almanac-ui/icons'
import { AddStrategiesModal } from '../AddStrategiesModal/AddStrategiesModal'
import { useModals } from 'almanac-ui/Modal'

interface StrategyAndContextSelectionProps {
  brandPlan: BrandPlan
  onNext: (
    strategyType: ContentStrategyType,
    strategyConfig: ContentStrategy,
    sources: CampaignSource[],
    themeDetails: string | null,
  ) => void
  onCancel: () => void
  initialSources: CampaignSource[]
  initialThemeDetails?: string | null
}

export const StrategyAndContextSelection = ({
  brandPlan,
  onNext,
  onCancel,
  initialSources,
  initialThemeDetails,
}: StrategyAndContextSelectionProps) => {
  const { openModal } = useModals()
  const [selectedStrategy, setSelectedStrategy] = useState<ContentStrategyType | null>(null)
  const [sources, setSources] = useState<CampaignSource[]>(initialSources)
  const [isUploading, setIsUploading] = useState(false)
  const [themeDetails, setThemeDetails] = useState<string>(initialThemeDetails ?? '')
  const [isAnalysisPending, setIsAnalysisPending] = useState(false)
  const onAnalysisPendingChange = useRef((isPending: boolean) => setIsAnalysisPending(isPending))
  onAnalysisPendingChange.current = (isPending: boolean) => setIsAnalysisPending(isPending)

  const contentStrategiesEntries = useMemo(() => {
    if (!brandPlan.content_strategies) return []
    return Object.entries(brandPlan.content_strategies).filter(
      (entry): entry is [ContentStrategyType, ContentStrategy] => entry[1] != null,
    )
  }, [brandPlan.content_strategies])

  const effectiveSelected = selectedStrategy || contentStrategiesEntries[0]?.[0]

  const handleNext = useCallback(() => {
    if (!effectiveSelected) return

    const config = brandPlan.content_strategies?.[effectiveSelected]
    if (!config) return

    onNext(effectiveSelected, config, sources, themeDetails || null)
  }, [effectiveSelected, brandPlan.content_strategies, onNext, sources, themeDetails])

  useEffect(() => {
    if (effectiveSelected && !selectedStrategy) {
      setSelectedStrategy(effectiveSelected)
    }
  }, [effectiveSelected, selectedStrategy])

  return (
    <>
      <Modal.Content className={Styles.content}>
        <div className={Styles.section}>
          <Heading level={4}>Strategy</Heading>
          <div className={Styles.strategySelectContainer}>
            <StrategySelect
              brandPlan={brandPlan}
              selectedKey={selectedStrategy}
              onSelectionChange={(key) => setSelectedStrategy(key as ContentStrategyType)}
            />
            <Button
              frontIcon={Settings}
              variant="secondary"
              size="lg"
              onPress={() => openModal(AddStrategiesModal, { newStack: true })}
            >
              Manage
            </Button>
          </div>
        </div>
        <div className={Styles.section}>
          <Heading level={4}>Tell us more about the campaign</Heading>
          <Input
            isTextarea
            name="campaignDescription"
            placeholder="Share any details that are relevant to the campaign you want to create..."
            size="lg"
            className={Styles.campaignDescriptionInput}
            value={themeDetails}
            onChange={(e) => setThemeDetails((e.target as HTMLTextAreaElement).value)}
          />
        </div>
        <div className={Styles.section}>
          <Heading level={4}>Add assets or webpages Blaze will analyze and build a campaign brief</Heading>
          <AddContext
            sources={sources}
            setSources={setSources}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onAnalysisPendingChange={onAnalysisPendingChange.current}
            brandKitId={brandPlan.brand_kit?.id}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={onCancel} variant="tertiary" size="lg">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleNext}
            isDisabled={!effectiveSelected || isUploading || isAnalysisPending}
          >
            Continue
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
